import React from "react";

import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import type { ChannelNode } from "~/common/SlackChannelSelect";
import type { ConversationNode } from "~/common/TeamsConversationSelect";
import type { TeamNode } from "~/common/TeamsTeamSelect";
import type { ChannelNode as ZoomChannelNode } from "~/common/ZoomChannelSelect";
import { promptSlackAuth } from "~/lib/auth/index";
import { isTeams } from "~/lib/utils";
import { Button } from "~/ui";
import ArrowDropdown from "~/ui/ArrowDropdown";
import { Input } from "~/ui/legacyFellowIndex";

import UserEmailInputs from "./Email";
import MSTeamsInputs from "./MSTeams";
import { ReactComponent as Illustration } from "./ReminderIllo.svg";
import SlackInputs, { onSlackAuthFailed, refetchUserSlackIntegration } from "./Slack";
import { DeleteTaskButton, getTimeText, Header, TaskSummary, ValueDropdownItem } from "./Tasks";
import {
	NOTIFICATION_PATH_VALUES,
	type NotificationPathValueType,
	PATH_TEXT_MAP,
	type TaskType,
	type UserType,
} from "./types";
import ZoomInputs from "./Zoom";

import stylesTimeSaver from "~/stream/StreamSettings/Automations/TimeSaver.module.css";

import styles from "./MeetingReminders.module.css";

const MAX_NUM_REMINDERS = 5;
const REMINDER_VALUES = [-15, -30, -60, -120, -240, -1440, -2880, -4320] as const; // minutes
export type ReminderValueType = (typeof REMINDER_VALUES)[number];

function EmptyState({ onClick }: { onClick: () => void }) {
	return (
		<>
			<Illustration />
			<p className={styles.emptyStateText}>
				Before the meeting, remind attendees to add talking points and review the agenda.
			</p>
			<Button variant="primary" size="normal" className={styles.emptyStateButton} onClick={onClick}>
				Add first reminder
			</Button>
		</>
	);
}

interface ReminderProps {
	streamId: string;
	automationId: string;
	usersWithAccess: UserType[];
	viewerHasOffice: boolean;
	viewerHasSlack: boolean;
	viewerHasZoom: boolean;
	// -- task data --
	// common
	id?: string;
	delta?: ReminderValueType;
	notificationPath?: NotificationPathValueType;
	message?: string;
	// email
	allParticipants?: boolean;
	userIds?: string[];
	// slack
	slackTeamId?: string | null;
	channelId?: string;
	channelName?: string;
	// zoom
	zoomChannelId?: string;
	zoomChannelName?: string;
	// msteams
	team?: string;
	teamName?: string;
	conversation?: string;
	conversationName?: string;
	isUser?: boolean;
	// -- callbacks --
	onBack: () => void;
	onHome: () => void;
}

function Reminder({
	streamId,
	automationId,
	usersWithAccess,
	viewerHasOffice,
	viewerHasSlack,
	viewerHasZoom,
	// -- task data --
	// common
	id,
	delta = REMINDER_VALUES[0],
	notificationPath = NOTIFICATION_PATH_VALUES[0],
	message = "",
	// email
	allParticipants = false,
	userIds = [],
	// slack
	slackTeamId = "",
	channelId = "",
	channelName = "",
	// zoom
	zoomChannelId = "",
	zoomChannelName = "",
	// msteams
	team = "",
	teamName = "",
	conversation = "",
	conversationName = "",
	isUser = false,
	// -- callbacks --
	onBack,
	onHome,
}: ReminderProps) {
	// email specific fields
	const [allParticipantsValue, setAllParticipantsValue] = React.useState<boolean>(allParticipants);
	const onAllParticipantsChange = React.useCallback((checked: boolean) => setAllParticipantsValue(checked), []);

	const [userIdsValue, setUserIdsValue] = React.useState<string[]>(userIds);

	// slack specific fields
	const [channelValue, setChannelValue] = React.useState<ChannelNode>({
		id: channelId,
		teamId: slackTeamId,
		name: channelName,
		isPrivate: false,
		isUser: false,
	});

	// msteams specific fields
	const [teamValue, setTeamValue] = React.useState<TeamNode>({ id: team, name: teamName });
	const [conversationValue, setConversationValue] = React.useState<ConversationNode>({
		id: conversation,
		name: conversationName,
		isUser,
	});

	// zoom specific fields
	const [zoomChannelValue, setZoomChannelValue] = React.useState<ZoomChannelNode>({
		id: zoomChannelId,
		name: zoomChannelName,
		isUser: false,
	});

	// common fields
	const [deltaValue, setDeltaValue] = React.useState<ReminderValueType>(delta); // minutes
	const [pathValue, setPathValue] = React.useState<NotificationPathValueType>(notificationPath);
	const [messageValue, setMessageValue] = React.useState<string>(message);
	const onMessageChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => setMessageValue(e.target.value),
		[],
	);

	const input = React.useMemo(() => {
		/* eslint-disable camelcase */
		const commonInput = {
			streamId,
			automationId,
			task: {
				id,
				delta: deltaValue,
				data: {
					notification_path: pathValue,
					message: messageValue.trim(),
				},
			},
		};

		if (pathValue === NOTIFICATION_PATH_VALUES[0]) {
			// email
			if (!allParticipantsValue && !userIdsValue.length) return undefined;

			return {
				...commonInput,
				task: {
					...commonInput.task,
					data: {
						...commonInput.task.data,
						all_participants: allParticipantsValue,
						user_ids: allParticipantsValue ? [] : userIdsValue,
					},
				},
			};
		} else if (pathValue === NOTIFICATION_PATH_VALUES[1]) {
			// slack
			if (!channelValue.id || !channelValue.name) return undefined;

			return {
				...commonInput,
				task: {
					...commonInput.task,
					data: {
						...commonInput.task.data,
						slack_team_id: channelValue.teamId,
						channel_id: channelValue.id,
						channel_name: channelValue.name,
					},
				},
			};
		} else if (pathValue === NOTIFICATION_PATH_VALUES[2]) {
			// msteams
			if (!teamValue.id || !teamValue.name || !conversationValue.id || !conversationValue.name) return undefined;

			return {
				...commonInput,
				task: {
					...commonInput.task,
					data: {
						...commonInput.task.data,
						team: teamValue.id,
						team_name: teamValue.name,
						conversation: conversationValue.id,
						conversation_name: conversationValue.name,
						is_user: conversationValue.isUser,
					},
				},
			};
		} else if (pathValue === NOTIFICATION_PATH_VALUES[3]) {
			// zoom
			if (!zoomChannelValue.id || !zoomChannelValue.name) return undefined;

			return {
				...commonInput,
				task: {
					...commonInput.task,
					data: {
						...commonInput.task.data,
						zoom_channel_id: zoomChannelValue.id,
						zoom_channel_name: zoomChannelValue.name,
					},
				},
			};
		}

		/* eslint-enable camelcase */
		return undefined;
	}, [
		streamId,
		automationId,
		id,
		deltaValue,
		pathValue,
		allParticipantsValue,
		userIdsValue,
		messageValue,
		channelValue,
		teamValue,
		conversationValue,
		zoomChannelValue,
	]);

	const pathDropdownDisabled = Boolean(
		(isTeams && pathValue === NOTIFICATION_PATH_VALUES[1]) ||
			(!viewerHasOffice && pathValue === NOTIFICATION_PATH_VALUES[2]),
	);

	// for slack auth prompt
	const [slackAuthRequired, setSlackAuthRequired] = React.useState<boolean>(!viewerHasSlack);
	const onSlackAuthSuccess = React.useCallback(() => {
		const callback = () => {
			setSlackAuthRequired(false);
			setPathValue(NOTIFICATION_PATH_VALUES[1]);
		};

		refetchUserSlackIntegration(callback);
	}, []);

	const onOpenSlackAuth = React.useCallback(
		() => promptSlackAuth().then(onSlackAuthSuccess, onSlackAuthFailed),
		[onSlackAuthSuccess],
	);

	return (
		<>
			<Header
				name="Reminder"
				backName="Pre-meeting Reminders"
				onHome={onHome}
				onBack={onBack}
				input={input}
				showButtons
			/>

			<div className={cx(styles.box, stylesTimeSaver.box)}>
				<div className={cx(styles.row, stylesTimeSaver.row, styles.tight)}>
					<label className={stylesTimeSaver.label}>Send reminder:</label>
					<ArrowDropdown
						compact
						heading="Send reminder"
						text={getTimeText(deltaValue)}
						className={stylesTimeSaver.dropdown}
						textClassName={stylesTimeSaver.dropdownText}
						popupClassName={stylesTimeSaver.dropdownPopup}
					>
						{REMINDER_VALUES.map(value => (
							<ValueDropdownItem key={value} value={value} onClick={setDeltaValue}>
								{getTimeText(value)}
							</ValueDropdownItem>
						))}
					</ArrowDropdown>
				</div>

				<div
					className={cx(styles.row, stylesTimeSaver.row, {
						[styles.tight]: pathValue !== NOTIFICATION_PATH_VALUES[0],
					})}
				>
					<label className={stylesTimeSaver.label}>Send reminder by:</label>
					<ArrowDropdown
						compact
						heading="Send reminder by"
						text={PATH_TEXT_MAP[pathValue]}
						className={stylesTimeSaver.dropdown}
						textClassName={stylesTimeSaver.dropdownText}
						popupClassName={stylesTimeSaver.dropdownPopup}
						disabled={pathDropdownDisabled}
					>
						<ValueDropdownItem value={NOTIFICATION_PATH_VALUES[0]} onClick={setPathValue}>
							{PATH_TEXT_MAP[NOTIFICATION_PATH_VALUES[0]]}
						</ValueDropdownItem>
						{!isTeams && (
							<ValueDropdownItem
								value={NOTIFICATION_PATH_VALUES[1]}
								onClick={slackAuthRequired ? onOpenSlackAuth : setPathValue}
							>
								{PATH_TEXT_MAP[NOTIFICATION_PATH_VALUES[1]]}
							</ValueDropdownItem>
						)}
						{viewerHasOffice && (
							<ValueDropdownItem value={NOTIFICATION_PATH_VALUES[2]} onClick={setPathValue}>
								{PATH_TEXT_MAP[NOTIFICATION_PATH_VALUES[2]]}
							</ValueDropdownItem>
						)}
						{viewerHasZoom && (
							<ValueDropdownItem value={NOTIFICATION_PATH_VALUES[3]} onClick={setPathValue}>
								{PATH_TEXT_MAP[NOTIFICATION_PATH_VALUES[3]]}
							</ValueDropdownItem>
						)}
					</ArrowDropdown>
				</div>

				<hr className={styles.divider} />
				{pathValue === NOTIFICATION_PATH_VALUES[0] && (
					<UserEmailInputs
						allParticipantsValue={allParticipantsValue}
						onAllParticipantsChange={onAllParticipantsChange}
						userIdsValue={userIdsValue}
						onUserIdsChange={setUserIdsValue}
						usersWithAccess={usersWithAccess}
					/>
				)}
				{pathValue === NOTIFICATION_PATH_VALUES[1] && <SlackInputs channel={channelValue} onChange={setChannelValue} />}
				{pathValue === NOTIFICATION_PATH_VALUES[2] && (
					<MSTeamsInputs
						team={teamValue}
						conversation={conversationValue}
						onTeamChange={setTeamValue}
						onConversationChange={setConversationValue}
					/>
				)}
				{pathValue === NOTIFICATION_PATH_VALUES[3] && (
					<ZoomInputs channel={zoomChannelValue} onChange={setZoomChannelValue} />
				)}
				<hr className={styles.divider} />

				<label className={cx(styles.row, stylesTimeSaver.row, styles.tight, stylesTimeSaver.label)}>
					Custom message:
				</label>
				<div className={cx(styles.row, stylesTimeSaver.row)}>
					<Input
						className={styles.input}
						value={messageValue}
						onChange={onMessageChange}
						placeholder="Set a custom message to go with your reminder…"
						maxLength={255}
						name="meeting_reminder_message"
					/>
				</div>
			</div>

			{id && (
				<div className={styles.alignedRightContainer}>
					<DeleteTaskButton taskId={id} name="reminder" callback={onBack} />
				</div>
			)}
		</>
	);
}

interface MeetingRemindersProps {
	streamId: string;
	automationId: string;
	usersWithAccess: UserType[];
	viewerHasOffice: boolean;
	viewerHasSlack: boolean;
	viewerHasZoom: boolean;
	// tasks data
	tasks?: readonly TaskType[];
	// callbacks
	onBack: () => void;
}

export default function MeetingReminders({
	streamId,
	automationId,
	usersWithAccess,
	viewerHasOffice,
	viewerHasSlack,
	viewerHasZoom,
	// task data
	tasks = [],
	// callbacks
	onBack,
}: MeetingRemindersProps) {
	const numTasks = tasks.length;

	const [taskConfig, setTaskConfig] = React.useState<string | null>(null); // task.id or "new"
	const clearTaskConfig = React.useCallback(() => setTaskConfig(null), []);
	const onNewTaskConfig = React.useCallback(() => setTaskConfig("new"), []);

	if (taskConfig) {
		const commonProps = {
			streamId,
			automationId,
			usersWithAccess,
			onBack: clearTaskConfig,
			onHome: onBack,
			viewerHasOffice,
			viewerHasSlack,
			viewerHasZoom,
		};

		if (taskConfig === "new") {
			return <Reminder {...commonProps} />;
		}

		const task = tasks.find(t => t.id === taskConfig);

		if (task) {
			const taskData = {
				...task.data,
				id: task.id,
				delta: task.delta as ReminderValueType,
				notificationPath: task.data?.notificationPath as NotificationPathValueType,
				userIds: [...(task.data?.userIds ?? [])],
			};

			return <Reminder {...taskData} {...commonProps} />;
		}
	}

	return (
		<>
			<Header name="Pre-meeting Reminders" onHome={onBack} />
			{numTasks === 0 && <EmptyState onClick={onNewTaskConfig} />}
			{numTasks > 0 && tasks.map(task => <TaskSummary key={task.id} task={task} onClick={setTaskConfig} />)}
			{numTasks > 0 && numTasks < MAX_NUM_REMINDERS && (
				<Button size="wide" variant="secondary" onClick={onNewTaskConfig} className={styles.newTaskButton}>
					<FontAwesomeIcon icon={faPlus} style={{ marginRight: "0.5ch" }} />
					Add another reminder
				</Button>
			)}
		</>
	);
}
